<template>
  <!-- voce a sinistra mostra menu on hover -->
  <transition name="fade">
    <div
      v-show="!layoutStore.isLanding"
      class="fixed-corner fixed-corner--share fixed-corner--desktop"
      @mouseenter="handleShareMouseEnter"
      @mouseleave="handleShareMouseLeave">
      <span class="fixed-corner__text">{{ t("general.share") }}</span>
      <transition name="dropdown-fade">
        <div
          v-if="shareOpen"
          class="fixed-corner__dropdown">
          <a
            :href="t('header.shareLinks.facebook')"
            class="fixed-corner__link"
            target="_blank"
            rel="noopener noreferrer"
            @click="onContactClick('facebook')">{{ t("header.socialMedia.facebook") }}</a>
          <a
            href="https://www.instagram.com/venetacucine/"
            class="fixed-corner__link"
            target="_blank"
            rel="noopener noreferrer"
            @click="onContactClick('instagram')">{{
              t("header.socialMedia.instagram") }}</a>
          <a
            :href="t('header.shareLinks.x')"
            class="fixed-corner__link"
            target="_blank"
            rel="noopener noreferrer"
            @click="onContactClick('x')">{{
              t("header.socialMedia.x") }}</a>
          <a
            href="https://www.linkedin.com/company/veneta-cucine-spa"
            class="fixed-corner__link"
            target="_blank"
            rel="noopener noreferrer"
            @click="onContactClick('linkedin')">{{
              t("header.socialMedia.linkedin") }}</a>

          <a
            :href="t('header.shareLinks.pinterest')"
            class="fixed-corner__link"
            target="_blank"
            rel="noopener noreferrer"
            @click="onContactClick('pinterest')">{{ t("header.socialMedia.pinterest") }}</a>

          <a
            href="https://www.tiktok.com/@venetacucineofficial?_r=1&_t=ZN-93QEIYSnE5m"
            class="fixed-corner__link"
            target="_blank"
            rel="noopener noreferrer"
            @click="onContactClick('tiktok')">TikTok</a>
        </div>
      </transition>
    </div>
  </transition>

  <!-- voce a destra mostra info on hover -->
  <transition name="fade">
    <div
      v-show="!layoutStore.isLanding"
      class="fixed-corner fixed-corner--info fixed-corner--desktop"
      @mouseenter="handleInfoMouseEnter"
      @mouseleave="handleInfoMouseLeave">
      <span class="fixed-corner__text">{{ t("general.info") }}</span>
      <transition name="dropdown-fade">
        <div
          v-if="infoOpen"
          class="fixed-corner__dropdown">
          <nuxt-link
            :to="localePath({ name: 'store-locator' })"
            class="fixed-corner__link">{{
              t("header.fixedCorner.dealersLink")
            }}</nuxt-link>
          <nuxt-link
            :to="localePath({ name: 'catalogues' })"
            class="fixed-corner__link">{{
              t("header.fixedCorner.catalogsLink")
            }}</nuxt-link>
          <nuxt-link
            :to="localePath({ name: 'contacts' })"
            class="fixed-corner__link">{{
              t("header.fixedCorner.contactLink")
            }}</nuxt-link>
        </div>
      </transition>
    </div>
  </transition>

  <transition name="fade">
    <div
      v-show="!layoutStore.isLanding"
      class="fixed-corner fixed-corner--info fixed-corner--mobile"
      @click="toggleInfo">
      <info-icon class="info-icon" />
      <transition name="dropdown-fade">
        <div
          v-if="infoOpen"
          class="fixed-corner__dropdown fixed-corner__dropdown--mobile">
          <nuxt-link
            :to="localePath({ name: 'store-locator' })"
            class="fixed-corner__link">{{
              t("header.fixedCorner.dealersLink")
            }}</nuxt-link>
          <nuxt-link
            :to="localePath({ name: 'catalogues' })"
            class="fixed-corner__link">{{
              t("header.fixedCorner.catalogsLink")
            }}</nuxt-link>
          <nuxt-link
            :to="localePath({ name: 'contacts' })"
            class="fixed-corner__link">{{
              t("header.fixedCorner.contactLink")
            }}</nuxt-link>
        </div>
      </transition>
    </div>
  </transition>

  <div
    :class="[
      'dynamic-header-wrapper',
      { 'dynamic-header-wrapper--search-open': searchOpen },
      { 'dynamic-header-wrapper--menu-open': menuOpen },
      { 'dynamic-header-wrapper--closing': isClosing },
    ]">
    <header
      :class="[
        'dynamic-header',
        { 'dynamic-header--expanded': searchOpen || menuOpen || isClosing },
      ]">
      <div class="dynamic-header__content">
        <!-- Left side controls -->

        <transition name="fade">
          <div
            v-show="!layoutStore.isLanding"
            class="dynamic-header__left">
            <!-- Close button (only visible when expanded) -->
            <header-close-button
              :is-open="closeButtonOpen"
              @click="toggleMenu"
              @close="closeAll" />

            <div
              v-if="!(searchOpen || menuOpen)"
              class="dynamic-header__divider"></div>

            <button
              v-if="!(searchOpen || menuOpen)"
              class="dynamic-header__control"
              :class="{ active: searchOpen }"
              data-testid="header-search-toggle"
              @click="toggleSearch">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none">
                <path
                  d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round" />
              </svg>
              <span class="dynamic-header__control-text">{{ t("general.search") }}</span>
            </button>
          </div>
        </transition>

        <!-- Center logo -->
        <component
          :is="isHome ? 'h1' : 'div'"
          class="dynamic-header__logo">
          <nuxt-link
            v-if="!isLandingsRoute"
            :to="localePath('/')"
            @click="handleLogoClick">
            <span class="dynamic-header__logo-text">Veneta Cucine</span>
            <logo-icon
              class="dynamic-header__logo-svg"
              aria-hidden="true" />
          </nuxt-link>
          <logo-icon
            v-else
            class="dynamic-header__logo-svg" />
        </component>

        <!-- Right side -->
        <transition name="fade">
          <div
            v-show="!layoutStore.isLanding"
            class="dynamic-header__right">
            <nuxt-link
              :to="localePath({ name: 'core-values' })"
              class="dynamic-header__green">
              {{ t("general.coreValues") }}
            </nuxt-link>
          </div>
        </transition>
      </div>

      <!-- Search dropdown -->
      <div
        v-if="searchOpen || (isClosing && wasSearchOpen)"
        class="dynamic-header__menu-dropdown dynamic-header__menu-dropdown--search"
        data-lenis-prevent>
        <search-component
          :is-open="searchOpen"
          @navigate="handleSearchNavigate" />
      </div>

      <!-- Menu dropdown -->
      <div
        v-if="menuOpen || (isClosing && wasMenuOpen)"
        class="dynamic-header__menu-dropdown"
        data-lenis-prevent>
        <menu-component @close-menu="handleMenuNavigation" />
      </div>
    </header>
  </div>
</template>

<script setup>
  import { useNuxtApp, useRouter } from '#imports'
  import InfoIcon from '~/assets/svgs/icons/info.svg'
  import LogoIcon from '~/assets/svgs/logo.svg'
  import MenuComponent from '~/components/Menu.vue'
  import SearchComponent from '~/components/Search.vue'
  import { useScrollStore } from '~/store/scroll'
  import { useSearchStore } from '~/store/search'
  import { pushDataLayer } from '~/utils/tracking'

  import HeaderCloseButton from './HeaderCloseButton.vue'

  const localePath = useLocalePath()
  const { t } = useI18n()
  const router = useRouter()
  const { bus } = useNuxtApp()

  defineOptions({
    components: {
      HeaderCloseButton
    }
  })

  // Reactive state
  const searchOpen = ref(false)
  const menuOpen = ref(false)
  const shareOpen = ref(false)
  const infoOpen = ref(false)
  const isScrolled = ref(false)
  const isClosing = ref(false)
  const wasSearchOpen = ref(false)
  const wasMenuOpen = ref(false)
  const scrollStore = useScrollStore()
  const menuLockActive = ref(false)
  const overlayWasOpen = computed(() => wasMenuOpen.value || wasSearchOpen.value)
  const overlayOpen = computed(() => menuOpen.value || searchOpen.value)
  const searchStore = useSearchStore()
  const isLandingsRoute = computed(() => {
    const name = router.currentRoute.value?.name
    return typeof name === 'string' && name.startsWith('landings-')
  })
  const isHome = computed(() => {
    const name = router.currentRoute.value?.name
    return typeof name === 'string' && name.startsWith('index')
  })
  const layoutStore = useLayoutStore()

  const refreshScroll = async () => {
    scrollStore.locked = false
    await nextTick()
    bus?.emit?.('scroll:refresh')
  }

  const shouldLockMenu = computed(() => {
    return overlayOpen.value || (isClosing.value && overlayWasOpen.value)
  })

  const closeButtonOpen = computed(() => {
    return overlayOpen.value || (isClosing.value && overlayWasOpen.value)
  })

  watch(shouldLockMenu, shouldLock => {
    if (shouldLock && !menuLockActive.value) {
      menuLockActive.value = true
      scrollStore.locked = true
    } else if (!shouldLock && menuLockActive.value) {
      menuLockActive.value = false
      scrollStore.locked = false
    }
  })

  watch(overlayOpen, open => {
    if (!open && !isClosing.value) {
      refreshScroll()
    }
  })

  // Methods
  const closeAll = () => {
    // Store which dropdowns were open before closing
    wasSearchOpen.value = searchOpen.value
    wasMenuOpen.value = menuOpen.value

    // Clear search state when header closes
    searchStore.reset(true)

    // Start the closing sequence
    isClosing.value = true

    // After dropdown content slides out, close the dropdowns
    setTimeout(() => {
      searchOpen.value = false
      menuOpen.value = false
      isClosing.value = false
      wasSearchOpen.value = false
      wasMenuOpen.value = false

      menuLockActive.value = false
      scrollStore.locked = false
      refreshScroll()
    }, 300) // Duration of slide-out animation
  }

  const toggleDropdown = dropdown => {
    const dropdowns = {
      search: searchOpen,
      menu: menuOpen,
      info: infoOpen
    }

    // If we're currently closing, don't allow new toggles
    if (isClosing.value) {
      return
    }

    // Special handling for info dropdown - it should not close menu/search
    if (dropdown === 'info') {
      infoOpen.value = !infoOpen.value
      return
    }

    // Close all other dropdowns (except info)
    Object.entries(dropdowns).forEach(([ key, ref ]) => {
      if (key !== dropdown && key !== 'info') {
        ref.value = false
      }
    })

    // Toggle target dropdown
    dropdowns[dropdown].value = !dropdowns[dropdown].value
  }

  const toggleSearch = () => toggleDropdown('search')
  const toggleMenu = () => toggleDropdown('menu')
  const toggleInfo = () => toggleDropdown('info')

  const handleMenuNavigation = () => {
    if (isClosing.value || (!menuOpen.value && !searchOpen.value)) {
      return
    }

    closeAll()
  }

  let navigationTimeout = null
  const DROPDOWN_CLOSE_DURATION = 320

  const navigateWithClose = path => {
    if (!path) {
      return
    }

    const executeNavigation = () => {
      router.push(path)
      navigationTimeout = null
    }

    const shouldDelay = menuOpen.value || searchOpen.value || isClosing.value

    if (shouldDelay) {
      closeAll()
      if (navigationTimeout) {
        clearTimeout(navigationTimeout)
      }
      navigationTimeout = setTimeout(executeNavigation, DROPDOWN_CLOSE_DURATION)
    } else {
      executeNavigation()
    }
  }

  const isModifiedClick = event => {
    if (!event) {
      return false
    }

    return (
      event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button === 1
    )
  }

  const handleNavigationClick = (event, path) => {
    if (isModifiedClick(event)) {
      return
    }

    event?.preventDefault?.()
    navigateWithClose(path)
  }

  const handleSearchNavigate = path => {
    navigateWithClose(path)
    closeAll()
  }

  const handleLogoClick = event => {
    handleNavigationClick(event, '/')
  }

  const unregisterAfterEach = router.afterEach(async () => {
    await refreshScroll()
  })

  // Timeout references for delayed closing
  let shareTimeout = null
  let infoTimeout = null

  const handleShareMouseEnter = () => {
    // Clear any pending close timeout
    if (shareTimeout) {
      clearTimeout(shareTimeout)
      shareTimeout = null
    }
    shareOpen.value = true
  }

  const handleShareMouseLeave = () => {
    // Add a small delay before closing
    shareTimeout = setTimeout(() => {
      shareOpen.value = false
      shareTimeout = null
    }, 150)
  }

  const handleInfoMouseEnter = () => {
    // Clear any pending close timeout
    if (infoTimeout) {
      clearTimeout(infoTimeout)
      infoTimeout = null
    }

    infoOpen.value = true
  }

  const handleInfoMouseLeave = () => {
    // Add a small delay before closing
    infoTimeout = setTimeout(() => {
      infoOpen.value = false
      infoTimeout = null
    }, 150)
  }

  // Handle scroll for dynamic behavior
  const handleScroll = () => {
    isScrolled.value = window.scrollY > 100
  }

  const currentRoute = useRoute()
  const route = useRouteStore()
  const env = useRuntimeConfig().public

  const onContactClick = type => {
    pushDataLayer({
      page_path: currentRoute?.fullPath,
      page_location: `${env?.BASE_URL}${currentRoute?.fullPath}`,
      page_referrer: !!route?.from && route?.from?.fullPath ? `${env.BASE_URL}${route?.from.fullPath}` : document?.referrer,
      ...(type == 'instagram'
        ? {
          event: 'click_instagram_header'
        }
        : {}),
      ...(type == 'facebook'
        ? {
          event: 'click_facebook_header'
        }
        : {}),
      ...(type == 'linkedin'
        ? {
          event: 'click_linkedin_header'
        }
        : {}),
      ...(type == 'x'
        ? {
          event: 'click_x_header'
        }
        : {}),
      ...(type == 'youtube'
        ? {
          event: 'click_youtube_header'
        }
        : {}),
      ...(type == 'tiktok'
        ? {
          event: 'click_tiktok_header'
        }
        : {}),
      ...(type == 'pinterest'
        ? {
          event: 'click_pinterest_header'
        }
        : {})

    })
  }

  onMounted(() => {
    window.addEventListener('scroll', handleScroll)
  })

  onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll)

    // Clean up any pending timeouts
    if (shareTimeout) {
      clearTimeout(shareTimeout)
    }
    if (infoTimeout) {
      clearTimeout(infoTimeout)
    }

    if (menuLockActive.value) {
      menuLockActive.value = false
      scrollStore.locked = false
    }

    if (navigationTimeout) {
      clearTimeout(navigationTimeout)
    }

    unregisterAfterEach?.()
  })
</script>

<style lang="scss" scoped>
  .fixed-corner {
    position: fixed;
    top: 16px;
    color: white;
    mix-blend-mode: difference;
    font-size: 14px;
    font-weight: 500;
    text-transform: uppercase;
    z-index: 999;
    opacity: 1;
    visibility: visible;
    transition: all 0.42s cubic-bezier(0.4, 0, 0.2, 1);

    @include up-to-desktop {
      &--desktop {
        display: none;
      }
    }

    &--mobile {
      z-index: 1005 !important;

      // mix-blend-mode: unset !important;
      width: 20px;
      height: 20px;
      top: 32px;
    }

    @include from-desktop {
      &--mobile {
        display: none;
        width: 0;
        height: 0;
        overflow: hidden;
      }
    }

    // Add delay to reappearing when closing
    .dynamic-header-wrapper--closing & {
      transition: all 0.42s cubic-bezier(0.4, 0, 0.2, 1) 0.3s;
      z-index: 1003;
    }

    &--share {
      left: var(--grid-margin);
    }

    &--info {
      right: calc(var(--grid-margin) + 8px);
    }

    &__text {
      cursor: pointer;
      padding: 8px 0;
      display: inline-block;
    }

    &__dropdown {
      position: absolute;
      top: calc(100% + 4px);
      padding: 8px 0;
      min-width: 120px;
      display: flex;
      flex-direction: column;
      gap: 4px;
      mix-blend-mode: difference;
      z-index: 1000;

      .fixed-corner--share & {
        left: 0;
      }

      .fixed-corner--info & {
        right: 0;
      }

      &--mobile {
        top: calc(100% + 16px) !important;
      }
    }

    &__link {
      display: block;
      padding: 8px 0;
      font-family: var(--font-family-poppins);
      font-size: 14px;
      color: white;
      text-decoration: none;
      border-radius: 4px;
      transition: text-decoration-color 0.2s ease;

      .fixed-corner--info & {
        text-align: right;
      }

      &:hover {
        text-decoration: underline;
        text-underline-offset: 4px;
      }
    }
  }

  // Hide the old corner buttons completely
  .dynamic-header__share-corner,
  .dynamic-header__info-corner {
    display: none;
  }

  // Hide corner buttons when header goes fullscreen
  .dynamic-header-wrapper--search-open .dynamic-header__share-corner,
  .dynamic-header-wrapper--search-open .dynamic-header__info-corner,
  .dynamic-header-wrapper--menu-open .dynamic-header__share-corner,
  .dynamic-header-wrapper--menu-open .dynamic-header__info-corner {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
  }

  // Add delay to corner buttons when closing
  .dynamic-header-wrapper--closing .dynamic-header__share-corner,
  .dynamic-header-wrapper--closing .dynamic-header__info-corner {
    @include from-desktop {
      transition: all 0.42s cubic-bezier(0.4, 0, 0.2, 1) 0.3s;
    }

    @include up-to-desktop {
      transition: all 0.42s cubic-bezier(0.4, 0, 0.2, 1);
    }
  }

  .dynamic-header__corner-trigger {
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px 16px;
    border-radius: 4px;
    font-family: var(--font-family-poppins);
    font-size: 14px;
    font-weight: 500;
    text-transform: uppercase;
    color: white;
    transition: none;
  }

  .dynamic-header__corner-dropdown {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    background: none;
    backdrop-filter: none;
    border: none;
    box-shadow: none;
    border-radius: 4px;
    padding: 8px;
    min-width: 120px;
    z-index: 10;
    filter: none;
  }

  // Vue transition animations for dropdown fade
  .dropdown-fade-enter-active,
  .dropdown-fade-leave-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .dropdown-fade-enter-from {
    opacity: 0;
    transform: translateY(-10px);
  }

  .dropdown-fade-enter-to {
    opacity: 1;
    transform: translateY(0);
  }

  .dropdown-fade-leave-from {
    opacity: 1;
    transform: translateY(0);
  }

  .dropdown-fade-leave-to {
    opacity: 0;
    transform: translateY(-10px);
  }

  .dynamic-header__info-corner .dynamic-header__corner-dropdown {
    right: 0;
    left: auto;
  }

  .dynamic-header__corner-link {
    display: block;
    padding: 8px 12px;
    font-family: var(--font-family-poppins);
    font-size: 14px;
    color: white;
    text-decoration: none;
    border-radius: 4px;
    transition: text-decoration 0.2s ease;

    &:hover {
      text-decoration: underline;
    }
  }

  .dynamic-header-wrapper {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    margin: 0 auto;
    z-index: 1000;
    width: 60%;
    height: auto;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    @media (width <= 1023px) {
      padding: 0 16px;
      width: 100%;

      // transition: all 0s !important;
    }

    // When expanded, modify header positioning - keep logo centered
    &--search-open,
    &--menu-open {
      width: 100% !important;

      .dynamic-header--expanded {
        padding: 0;

        @media (width >= 1024px) {
          width: 100% !important;

          // left: 50% !important;
          transform: translateX(-50%) !important;
          max-width: none !important;
          min-width: auto !important;
          top: 0 !important;
          border-radius: 0;

          .dynamic-header__content {
            box-shadow: 0 0 14px 0 rgb(74, 74, 73, 0.2);
          }
        }

        .dynamic-header__content {
          padding-left: 16px;
          padding-right: 16px;
          box-shadow: 0 0 14px 0 rgb(74, 74, 73, 0.2);
          border-radius: 8px;

          @media (width >= 1024px) {
            border-radius: 0 0 8px 8px;
          }
        }
      }
    }
  }

  .dynamic-header {
    position: relative;
    z-index: 1001;
    transition: all 0.42s cubic-bezier(0.25, 0.46, 0.45, 0.94);

    @include up-to-desktop {
      transition: all 0s;
    }

    // Add delay to header animations when closing
    .dynamic-header-wrapper--closing & {
      @include from-desktop {
        transition: all 0.42s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.3s;
      }

      // @include up-to-desktop {
      //   transition: all 0s;
      // }
    }

    // Desktop: Attached to top with bottom rounded corners, 50% width expanding to full screen
    @include from-desktop {
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      background: white;
      backdrop-filter: blur(20px);
      border-radius: 0 0 20px 20px;
      border-top: none;
      box-shadow: 0 8px 32px rgb(0, 0, 0, 0.1);
      padding: 0 24px;
      height: 70px;
      width: 50vw;
      min-width: 50vw !important;
    }

    // Mobile: full width with rounded corners
    @include up-to-desktop {
      top: 12px;
      left: 0;
      transform: none;
      width: 100%;
      background: white;
      backdrop-filter: blur(20px);
      border-radius: 8px;
      border: 1px solid rgb(203, 204, 205, 0.3);
      box-shadow: 0 4px 16px rgb(0, 0, 0, 0.1);
      padding: 0 16px;
      height: 60px;
      margin: 0 auto;
    }

    &__overlay {
      position: fixed;
      inset: 0;
      background: rgb(0, 0, 0, 0.1);
      z-index: 999;
      opacity: 1;
      transition: opacity 0.3s ease;
    }

    &__content {
      align-items: center;
      justify-content: space-between;

      //
      display: grid;
      grid-template-columns: minmax(0, auto) minmax(0, auto) minmax(0, 20px);

      // z-index: 1002;
      height: 100%;
      background: white;

      @include mq(medium) {
        grid-template-columns: repeat(3, minmax(0, 1fr));
      }
    }

    &__left {
      display: flex;
      align-items: center;
      gap: 12px;

      @media (width <= 1023px) {
        gap: 12px;
      }
    }

    &__divider {
      width: 1px;
      height: 24px;
      background-color: var(--color-dark-grey);
      opacity: 0.3;
    }

    &__right {
      display: flex;
      align-items: center;
      gap: 16px;
      justify-content: flex-end;
      position: relative;
    }

    .is-landing & &__logo {
      grid-column: span 3;
    }

    &__logo {
      margin: 0; // neutralizza il margin-block di default introdotto da <h1> (desktop)

      @include mq($until: medium) {
        position: absolute;
        z-index: 0;
        inset: 0;
        width: max-content;
        margin: auto;
      }

      @media (width <= 350px) {
        > a {
          position: relative;
          left: 15px;
        }
      }

      a.router-link-exact-active {
        opacity: 1;
      }

      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      z-index: 2;
      transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);

      &-svg {
        height: 16px;
        width: auto;
        color: currentcolor;

        @include mq(tablet) {
          height: 20px;
        }
      }

      // Image-replacement: testo brand per crawler/screen reader, non visibile
      &-text {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }
    }

    &__green {
      font-family: var(--font-family-poppins);
      font-size: 14px;
      font-weight: 500;
      color: #88b276;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      text-decoration: none;
      white-space: nowrap;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: color 0.6s;

      &:hover {
        color: #3f5f31;
      }

      @media (width <= 1023px) {
        display: none;
      }
    }

    &__control {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      background: none;
      border: none;
      cursor: pointer;
      padding: 0;
      border-radius: 12px;

      // height: 40px;
      // width: 40px;
      transition: background-color 0.2s ease;

      &:hover,
      &.active {
        background: rgb(220, 8, 20, 0.1);
      }

      svg {
        color: #1d1d1b;
        transition: color 0.2s ease;
      }

      &:hover svg,
      &.active svg {
        color: #dc0814;
      }
    }

    &__control-text {
      display: none;
    }

    &__hamburger {
      display: flex;
      flex-direction: column;
      gap: 3px;
      width: 24px;
      height: 18px;
      justify-content: center;

      span {
        display: block;
        height: 1px;
        background: currentcolor;
        transition: all 0.3s ease;
        transform-origin: center;

        .dynamic-header__control.active & {
          &:first-child {
            transform: rotate(45deg) translate(6px, 6px);
          }

          &:nth-child(2) {
            opacity: 0;
          }

          &:last-child {
            transform: rotate(-45deg) translate(6px, -6px);
          }
        }
      }
    }

    // Search dropdown styles
    &__search-dropdown {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      background: white;
      border-radius: 0 0 20px 20px;
      box-shadow: 0 8px 32px rgb(0, 0, 0, 0.1);
      padding: 24px;
      margin-top: 1px;
      transform: translateY(-100%);
      opacity: 0;
      animation: slide-in-delayed 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.42s forwards;

      @media (width <= 1023px) {
        padding: 16px;
        animation: slide-in-delayed 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
      }

      // Closing animation
      .dynamic-header-wrapper--closing & {
        @include from-desktop {
          animation: slide-out-up 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
        }

        @include up-to-desktop {
          animation: slide-out-up 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
        }
      }
    }

    &__search-field {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 24px;
      padding-bottom: 16px;
      border-bottom: 1px solid #cbcccd;
      margin-bottom: 24px;
    }

    &__search-input {
      flex: 1;
      border: none;
      background: none;
      font-family: var(--font-family-poppins);
      font-size: 40px;
      font-weight: 500;
      color: #1d1d1b;
      outline: none;

      &::placeholder {
        color: #1d1d1b;
        opacity: 0.6;
      }

      @media (width <= 1023px) {
        font-size: 24px;
      }
    }

    &__search-results {
      display: flex;
      flex-direction: column;
    }

    &__search-result {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 32px 0;
      border-bottom: 1px solid #cbcccd;
      cursor: pointer;
      transition: background-color 0.2s ease;

      &:hover {
        background: rgb(220, 8, 20, 0.02);
      }

      &:last-child {
        border-bottom: none;
      }
    }

    &__result-content {
      display: flex;
      flex-direction: column;
      gap: 8px;
      flex: 1;
    }

    &__result-category {
      font-family: var(--font-family-poppins);
      font-size: 14px;
      font-weight: 500;
      text-transform: uppercase;
      color: #4a4a49;
    }

    &__result-title {
      font-family: var(--font-family-poppins);
      font-size: 18px;
      font-weight: 400;
      color: #1d1d1b;
      line-height: 1.6;
    }

    &__result-arrow {
      width: 26px;
      height: 26px;
      flex-shrink: 0;
    }

    // Menu dropdown styles
    &__menu-dropdown {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      transform: translateY(-110%);
      animation: slide-in-delayed 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.42s forwards;
      z-index: -1;
      height: calc(100dvh - var(--header-height) + 4px);
      overflow: hidden auto;

      @media (width <= 1023px) {
        // padding: 16px;
        // max-height: 70vh;
        left: -16px;
        top: -12px;
        padding-top: 106px;
        background: white;
        width: 100vw;
        overflow-y: auto;
        overscroll-behavior: contain;
        -webkit-overflow-scrolling: touch;
        animation: slide-in-from-top 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
        background-color: var(--color-light-grey);
      }

      // Closing animation
      .dynamic-header-wrapper--closing & {
        animation: slide-out-up 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
      }

      &--search {
        overflow: auto;
        background-color: #f3f3f3;

        @include mq(medium) {
          max-height: calc(100dvh - var(--header-height));
        }
      }
    }

    &__menu-content {
      display: flex;
      flex-direction: column;
      gap: 40px;
    }

    &__menu-section {
      padding-bottom: 24px;
      border-bottom: 1px solid #cbcccd;
    }

    &__menu-title {
      font-family: var(--font-family-poppins);
      font-size: 16px;
      font-weight: 500;
      text-transform: uppercase;
      color: #dc0814;
      margin-bottom: 24px;
    }

    &__menu-categories {
      display: flex;
      flex-direction: column;
      gap: 21px;
    }

    &__menu-category {
      background: none;
      border: none;
      text-align: left;
      cursor: pointer;
      font-family: var(--font-family-poppins);
      font-size: 60px;
      font-weight: 500;
      text-transform: uppercase;
      color: #000;
      line-height: 1.25;
      transition: color 0.2s ease;

      &:hover {
        color: #dc0814;
      }

      @media (width <= 1023px) {
        font-size: 36px;
      }
    }

    &__menu-columns {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 40px;

      @media (width <= 1023px) {
        grid-template-columns: 1fr;
        gap: 32px;
      }
    }

    &__menu-column {
      display: flex;
      flex-direction: column;
      gap: 32px;
    }

    &__menu-group {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    &__menu-group-title {
      font-family: var(--font-family-poppins);
      font-size: 16px;
      font-weight: 500;
      text-transform: uppercase;
      color: #dc0814;
    }

    &__menu-link {
      font-family: var(--font-family-poppins);
      font-size: 18px;
      font-weight: 400;
      color: #1d1d1b;
      text-decoration: underline;
      text-underline-offset: 2px;
      transition: color 0.2s ease;

      &:hover {
        color: #dc0814;
      }
    }

    &__menu-languages {
      display: flex;
      align-items: center;
      gap: 23px;
      margin-top: 16px;
    }

    &__menu-lang {
      background: none;
      border: none;
      cursor: pointer;
      font-family: var(--font-family-poppins);
      font-size: 16px;
      font-weight: 500;
      text-transform: uppercase;
      color: #1d1d1b;
      transition: color 0.2s ease;

      &:hover,
      &.active {
        color: #dc0814;
      }
    }

    &__menu-card {
      display: flex;
      align-items: center;
      gap: 32px;
      background: white;
      border-radius: 4px;
      padding: 0 32px 0 0;

      @media (width <= 1023px) {
        flex-direction: column;
        padding: 16px;
      }
    }

    &__card-image {
      width: 356px;
      height: 267px;
      flex-shrink: 0;
      border-radius: 4px;
      overflow: hidden;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      @media (width <= 1023px) {
        width: 100%;
        height: 200px;
      }
    }

    &__card-placeholder {
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
    }

    &__card-placeholder-text {
      font-family: var(--font-family-poppins);
      font-size: 16px;
      color: #4a4a49;
      opacity: 0.6;
    }

    &__card-content {
      display: flex;
      flex-direction: column;
      gap: 16px;
      flex: 1;
    }

    &__card-title {
      font-family: var(--font-family-poppins);
      font-size: 16px;
      font-weight: 500;
      text-transform: uppercase;
      color: #dc0814;
    }

    &__card-description {
      font-family: var(--font-family-poppins);
      font-size: 20px;
      font-weight: 400;
      color: #1d1d1b;
      line-height: 1.6;
    }

    &__card-arrow {
      width: 26px;
      height: 26px;
      align-self: flex-start;
    }

    &__breadcrumb-container {
      position: fixed;
      top: 70px; // Position below desktop header
      left: 50%;
      transform: translateX(-50%);
      width: 50%;
      min-width: 600px;
      background: none;
      backdrop-filter: none;
      border: none;
      border-radius: 0;
      box-shadow: none;
      padding: 0 24px;
      z-index: 999;

      @media (width <= 1023px) {
        top: 76px; // Position below mobile header (60px + 16px margin)
        width: 50%;
        min-width: auto;
        padding: 0 16px;
      }
    }

    &__info-button {
      display: none;
      background: none;
      border: none;
      padding: 0;
      cursor: pointer;
      color: #1d1d1b;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      z-index: 1002;
      transition: color 0.2s ease;
      align-items: center;
      justify-content: center;

      @media (width <= 1023px) {
        display: flex;
      }

      &:hover,
      &.active {
        color: #dc0814;
      }

      svg {
        width: 20px;
        height: 20px;
      }
    }

    &__info-dropdown {
      position: absolute;
      top: calc(100% + 4px);
      right: 16px;
      padding: 8px 0;
      min-width: 120px;
      display: flex;
      flex-direction: column;
      gap: 4px;
      z-index: 1002;
      color: white;
      mix-blend-mode: difference;

      @media (width <= 1023px) {
        position: fixed;
        top: 84px; // Header height (60px) + top spacing (12px) + gap (12px)
      }
    }

    &__info-link {
      display: block;
      padding: 8px 0;
      font-family: var(--font-family-poppins);
      font-size: 14px;
      color: white;
      text-decoration: none;
      text-align: right;
      transition: text-decoration-color 0.2s ease;

      &:hover {
        text-decoration: underline;
        text-underline-offset: 4px;
      }
    }
  }

  // Animation keyframes
  @keyframes slide-in-from-top {
    from {
      transform: translateY(-100%);
    }

    to {
      transform: translateY(0);
    }
  }

  @keyframes slide-in-delayed {
    from {
      transform: translateY(-100%);

      // opacity: 0;
    }

    to {
      transform: translateY(0);

      // opacity: 1;
    }
  }

  @keyframes slide-out-up {
    from {
      transform: translateY(0);

      // opacity: 1;
    }

    to {
      transform: translateY(-100%);

      // opacity: 0;
    }
  }

  @keyframes slide-out-to-top {
    from {
      transform: translateY(0);
    }

    to {
      transform: translateY(-100%);
    }
  }
</style>
